import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { qformat, DATE_FORMATS, formatTimeAgo } from "views_shared/app/javascript/utils/dateFormatters";
import ActivityTimeline from "../shared/ActivityTimeline";
import OutletConfigModal from "../shared/OutletConfigModal";
import { daColor, PRESTIGE_LABELS } from "../shared/OutletRating";
import MergeConsole from "../merge_console/MergeConsole";

const SUPABASE_URL = () => globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;

// --- Formatting helpers

function actionBadge(autoAction, large) {
  const style = { backgroundColor: "transparent", fontSize: large ? "1rem" : undefined };

  switch (autoAction) {
    case "approve_and_notify":
      return (
        <span className="badge" style={{ ...style, border: "1px solid #198754", color: "#198754" }}>
          Approve + Feed & Notify
        </span>
      );
    case "approve":
      return (
        <span className="badge" style={{ ...style, border: "1px solid #fd7e14", color: "#fd7e14" }}>
          Approve
        </span>
      );
    case "approve_and_distribute":
      return (
        <span className="badge" style={{ ...style, border: "1px solid #0d6efd", color: "#0d6efd" }}>
          Approve + Feed
        </span>
      );
    case "ghost":
      return (
        <span className="badge" style={{ ...style, border: "1px solid #6c757d", color: "#6c757d" }}>
          Ghost
        </span>
      );
    case "manual_review":
      return (
        <span className="badge" style={{ ...style, border: "1px solid #6c757d", color: "#6c757d" }}>
          Manual Review
        </span>
      );
    default:
      return (
        <span className="badge bg-danger" style={{ fontSize: large ? "1rem" : undefined }}>
          No Config
        </span>
      );
  }
}

function statusBadge(row) {
  if (row.ghosted_at) return <span className="badge bg-danger">Ghosted</span>;
  if (row.distribution_approved_for_notifications_at) return <span className="badge bg-success">Notified</span>;
  if (row.distribution_approved_for_feed_at) return <span className="badge bg-primary">Feed</span>;
  if (row.approved_at) return <span className="badge bg-secondary">Approved</span>;
  if (row.needs_manual_approval_at) return <span className="badge bg-warning text-dark">Manual</span>;
  if (row.posted_at) return <span className="badge bg-info text-dark">Pending</span>;
  return <span className="badge bg-light text-dark">Draft</span>;
}

// --- Outlet header component

function OutletHeader({ outlet, onChangeConfig, onMerge }) {
  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-auto">
            {outlet.profile_image_url ? (
              <img
                src={outlet.profile_image_url}
                alt=""
                style={{ maxWidth: 160, maxHeight: 64, objectFit: "contain" }}
              />
            ) : (
              <div
                className="bg-light d-flex align-items-center justify-content-center text-muted"
                style={{ width: 80, height: 80, borderRadius: 8, fontSize: "2rem" }}
              >
                <i className="fa fa-newspaper" />
              </div>
            )}
          </div>
          <div className="col">
            <h1 className="h3 mb-1">{outlet.display_name}</h1>
            {outlet.domain && (
              <p className="text-muted mb-2">
                <i className="fa fa-globe me-1" />
                <a href={`https://${outlet.domain}`} target="_blank" rel="noopener noreferrer" className="text-muted">
                  {outlet.domain}
                </a>
              </p>
            )}
            <div className="d-flex align-items-center">
              {actionBadge(outlet.auto_action, true)}
              {outlet.set_free && <span className="badge border border-info text-info ms-1">Free</span>}
              {outlet.set_by_name && (
                <small className="text-muted ms-2">
                  Set by {outlet.set_by_name} on {qformat(outlet.set_at, DATE_FORMATS.date)}
                </small>
              )}
              <button type="button" className="btn btn-sm btn-outline-primary ms-3" onClick={onChangeConfig}>
                Change...
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger ms-2"
                onClick={onMerge}
                data-testid="merge-outlet-button"
              >
                <i className="fa fa-object-group me-1" />
                Merge
              </button>
              {/* The outlet's public page is also where its curation edit surface lives (the edit
                  affordances there are gated on the same admin-only update policy), so this is how a
                  curator reaches it from the admin index. */}
              <a
                href={`/media_outlets/${outlet.slug || outlet.id}`}
                className="btn btn-outline-secondary btn-sm ms-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa fa-external-link me-1" />
                View public page
              </a>
              {globalThis.Qwoted.V1_URL && outlet?.migrate_id && (
                <a
                  href={`${globalThis.Qwoted.V1_URL}/publications/${outlet.migrate_id}?version=v1`}
                  className="btn btn-outline-secondary btn-sm ms-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa fa-external-link me-1" />
                  V1
                </a>
              )}
            </div>
          </div>
          {outlet.prestige > 0 && (
            <div className="col-auto text-center px-3">
              <h3 className="mb-0">{"\u2605".repeat(outlet.prestige)}</h3>
              <small className="text-muted">{PRESTIGE_LABELS[outlet.prestige]}</small>
            </div>
          )}
          <div className="col-auto text-center px-3">
            <h3 className="mb-0" style={{ fontWeight: 600, color: daColor(outlet.domain_authority) }}>
              {outlet.domain_authority ?? "\u2014"}
            </h3>
            <small className="text-muted">DA</small>
          </div>
          <div className="col-auto text-center px-3">
            <h3 className="mb-0">{(outlet.request_count || 0).toLocaleString()}</h3>
            <small className="text-muted">Requests</small>
          </div>
          <div className="col-auto text-center px-3">
            <h3 className="mb-0 text-muted">{"\u2014"}</h3>
            <small className="text-muted">This Month</small>
          </div>
          <div className="col-auto text-center px-3">
            <h3 className="mb-0 text-muted">{"\u2014"}</h3>
            <small className="text-muted">Reporters</small>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Recent requests table component

function RecentRequests({ requests, totalCount, error }) {
  if (error) {
    return (
      <div className="card mb-4">
        <div className="card-header bg-white">
          <i className="fa fa-envelope me-2" />
          Recent Requests
        </div>
        <div className="card-body text-center py-4">
          <span className="text-danger small">{error}</span>
        </div>
      </div>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <div className="card mb-4">
        <div className="card-header bg-white">
          <i className="fa fa-envelope me-2" />
          Recent Requests
        </div>
        <div className="card-body text-muted text-center py-4">No requests found for this outlet.</div>
      </div>
    );
  }

  return (
    <div className="card mb-4">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <span>
          <i className="fa fa-envelope me-2" />
          Recent Requests
        </span>
        {totalCount > 20 && (
          <a href="/admin/requests" className="btn btn-sm btn-outline-secondary">
            View All ({totalCount.toLocaleString()})
          </a>
        )}
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-hover mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Request</th>
              <th>Reporter</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((req) => (
              <tr key={req.id}>
                <td>
                  <a href={`/admin/requests/${req.id}`} className="text-decoration-none">
                    {req.title || "Untitled"}
                  </a>
                </td>
                <td>
                  <small>{req.profile_name || "\u2014"}</small>
                </td>
                <td>
                  <small>{formatTimeAgo(req.posted_at)}</small>
                </td>
                <td>{statusBadge(req)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Top reporters sidebar component

function TopReporters({ requests }) {
  const reporters = useMemo(() => {
    if (!requests || requests.length === 0) return [];

    const grouped = {};
    requests.forEach((req) => {
      if (!req.profile_id) return;
      if (!grouped[req.profile_id]) {
        grouped[req.profile_id] = { profileId: req.profile_id, name: req.profile_name || "Unknown", count: 0 };
      }
      grouped[req.profile_id].count += 1;
    });

    return Object.values(grouped)
      .sort((left, right) => right.count - left.count)
      .slice(0, 5);
  }, [requests]);

  if (reporters.length === 0) return null;

  const maxCount = reporters[0]?.count || 1;

  return (
    <div className="card">
      <div className="card-header bg-white py-2">
        <h6 className="mb-0">
          <i className="fa fa-users me-2" />
          Top Reporters
        </h6>
      </div>
      <div className="card-body">
        {reporters.map((reporter, idx) => (
          <div key={reporter.profileId} className={idx < reporters.length - 1 ? "mb-2" : ""}>
            <div className="d-flex justify-content-between small mb-1">
              <span>{reporter.name}</span>
              <span className="text-muted">{reporter.count}</span>
            </div>
            <div className="progress" style={{ height: 6 }}>
              <div
                className="progress-bar"
                style={{
                  width: `${(reporter.count / maxCount) * 100}%`,
                  backgroundColor: "#4a3f6b",
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Main component

function MediaOutletShow({ mediaOutletId }) {
  const [outlet, setOutlet] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestCount, setRequestCount] = useState(0);
  const [requestsError, setRequestsError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        ...authenticatedHeadersHash(),
        "Content-Type": "application/json",
      };

      // Fetch outlet and recent requests in parallel
      const [outletRes, requestsRes] = await Promise.all([
        fetch(`${SUPABASE_URL()}/rest/v1/admin_outlets?id=eq.${mediaOutletId}`, {
          headers: {
            ...headers,
            Accept: "application/vnd.pgrst.object+json",
          },
        }),
        fetch(
          `${SUPABASE_URL()}/rest/v1/admin_review_queue?media_outlet_id=eq.${mediaOutletId}&posted_at=not.is.null&order=posted_at.desc&limit=20`,
          {
            headers: {
              ...headers,
              Prefer: "count=estimated",
            },
          },
        ),
      ]);

      if (!outletRes.ok) {
        if (outletRes.status === 406) {
          setError("Outlet not found");
        } else {
          setError(`Failed to load outlet (${outletRes.status})`);
        }
        return;
      }

      const outletData = await outletRes.json();
      setOutlet(outletData);

      if (requestsRes.ok) {
        const reqData = await requestsRes.json();
        setRequests(reqData);
        setRequestsError(null);

        const cr = requestsRes.headers.get("Content-Range");
        const totalStr = cr?.split("/")[1];
        if (totalStr) setRequestCount(parseInt(totalStr, 10));
      } else {
        setRequestsError(`Failed to load requests (${requestsRes.status})`);
      }
    } catch (err) {
      setError(err.message || "Failed to load outlet");
    } finally {
      setLoading(false);
    }
  }, [mediaOutletId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConfigSave = useCallback(() => {
    setShowConfigModal(false);
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fa fa-spinner fa-spin fa-2x text-muted" />
        <p className="text-muted mt-3">Loading outlet...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <i className="fa fa-exclamation-triangle fa-2x text-danger" />
        <p className="text-danger mt-3">{error}</p>
        <a href="/admin/media_outlets" className="btn btn-outline-primary">
          Back to Media Outlets
        </a>
      </div>
    );
  }

  if (!outlet) return null;

  return (
    <div>
      {/* Outlet header */}
      <OutletHeader
        outlet={outlet}
        onChangeConfig={() => setShowConfigModal(true)}
        onMerge={() => setShowMergeModal(true)}
      />

      <div className="row">
        {/* Left column: Recent requests */}
        <div className="col-lg-8">
          <RecentRequests requests={requests} totalCount={requestCount} error={requestsError} />
        </div>

        {/* Right column: Activity + Top reporters */}
        <div className="col-lg-4">
          <ActivityTimeline entityType="Id::MediaOutlet" entityId={String(mediaOutletId)} />

          <div className="mt-4">
            <TopReporters requests={requests} />
          </div>
        </div>
      </div>

      {/* Config modal */}
      {showConfigModal && (
        <OutletConfigModal
          outletId={outlet.id}
          outletName={outlet.display_name}
          currentConfig={
            outlet.config_id
              ? {
                  id: outlet.config_id,
                  auto_action: outlet.auto_action,
                  set_free: outlet.set_free,
                }
              : null
          }
          outletInfo={{
            prestige: outlet.prestige,
            domainAuthority: outlet.domain_authority,
            requestCount: outlet.request_count,
          }}
          onSave={handleConfigSave}
          onClose={() => setShowConfigModal(false)}
        />
      )}

      {/* Merge modal — reuses the merge console (and thus the shared merge client,
          normalizer, and polling flow) with this outlet prefilled as the source. */}
      <Modal show={showMergeModal} onHide={() => setShowMergeModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Merge media outlet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showMergeModal && (
            <MergeConsole
              initialType="Id::MediaOutlet"
              initialSourceId={String(outlet.id)}
              initialSourceLabel={outlet.display_name}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default MediaOutletShow;
